import {
  getBackendRoles,
  hasDashboardOrganisationAdminMembership,
  hasDashboardOrganisationMembership,
  isDashboardAdmin,
} from "./dashboardAccess";
import { isElectronRxProfileEmail } from "../patientProfileEmail";

export const DASHBOARD_OPERATION_KEYS = {
  organisationManage: "organisation.manage",
  studyView: "study.view",
  studyManage: "study.manage",
  subjectView: "subject.view",
  measurementView: "measurement.view",
  questionnaireView: "questionnaire.view",
  resultView: "result.view",
  exportView: "export.view",
  configurationManage: "configuration.manage",
};

const ROLE_OPERATOR = "operator";
const ROLE_RESEARCHER = "researcher";
const ROLE_CLINICIAN = "clinician";
const ROLE_ADMIN = "admin";
const DASHBOARD_ROLE_PREFIX = "dashboard-";

const MEMBER_READ_ROLES = new Set([
  ROLE_ADMIN,
  ROLE_CLINICIAN,
  ROLE_RESEARCHER,
  ROLE_OPERATOR,
]);

const RESULTS_AND_EXPORT_ROLES = new Set([
  ROLE_ADMIN,
  ROLE_CLINICIAN,
  ROLE_RESEARCHER,
]);
const SUBJECT_REMINDER_SEND_ROLES = new Set([ROLE_ADMIN, ROLE_CLINICIAN]);

function normalizeRole(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizePermissions(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .map((entry) =>
          typeof entry === "string" ? entry.trim() : String(entry || "").trim(),
        )
        .filter(Boolean),
    ),
  );
}

function hasResolvedPermissionSet(context) {
  return Array.isArray(context?.accountContext?.dashboardPermissions);
}

function hasResolvedPermission(context, operation) {
  return normalizePermissions(
    context?.accountContext?.dashboardPermissions,
  ).includes(operation);
}

function roleFromBackendRoles(context) {
  for (const role of getBackendRoles(context)) {
    const normalizedRole = normalizeRole(role);

    if (!normalizedRole.startsWith(DASHBOARD_ROLE_PREFIX)) {
      continue;
    }

    const candidateRole = normalizedRole.slice(DASHBOARD_ROLE_PREFIX.length);
    if (
      candidateRole === ROLE_ADMIN ||
      candidateRole === ROLE_CLINICIAN ||
      candidateRole === ROLE_RESEARCHER ||
      candidateRole === ROLE_OPERATOR
    ) {
      return candidateRole;
    }
  }

  return "";
}

export function resolveDashboardRole(
  context,
  { claims, email, isBootstrapAdmin = false } = {},
) {
  if (isBootstrapAdmin) {
    return ROLE_ADMIN;
  }

  if (isDashboardAdmin(context, { claims, email })) {
    return ROLE_ADMIN;
  }

  const accountRole = normalizeRole(context?.accountContext?.organisationRole);
  if (accountRole) {
    return accountRole;
  }

  const backendRole = roleFromBackendRoles(context);
  if (backendRole) {
    return backendRole;
  }

  const legacyRole = normalizeRole(claims?.role);
  if (
    legacyRole === ROLE_CLINICIAN ||
    legacyRole === ROLE_RESEARCHER ||
    legacyRole === ROLE_OPERATOR
  ) {
    return legacyRole;
  }

  return "";
}

function hasMemberScopedDashboardAccess(context, bridge = {}) {
  if (bridge.isBootstrapAdmin || isDashboardAdmin(context, bridge)) {
    return true;
  }

  return hasDashboardOrganisationMembership(context);
}

function hasInternalResultsBridge(email, context, bridge = {}) {
  if (!isElectronRxProfileEmail(email)) {
    return false;
  }

  return hasMemberScopedDashboardAccess(context, bridge);
}

export function hasDashboardOperationPermission(
  context,
  operation,
  bridge = {},
) {
  if (bridge.isBootstrapAdmin) {
    return true;
  }

  if (hasResolvedPermissionSet(context)) {
    if (!hasMemberScopedDashboardAccess(context, bridge)) {
      return false;
    }

    if (
      (operation === DASHBOARD_OPERATION_KEYS.resultView ||
        operation === DASHBOARD_OPERATION_KEYS.exportView) &&
      hasInternalResultsBridge(bridge.email, context, bridge)
    ) {
      return true;
    }

    return hasResolvedPermission(context, operation);
  }

  const role = resolveDashboardRole(context, bridge);
  const isPrivilegedAdmin =
    hasDashboardOrganisationAdminMembership(context) || role === ROLE_ADMIN;

  if (isPrivilegedAdmin) {
    return true;
  }

  switch (operation) {
    case DASHBOARD_OPERATION_KEYS.organisationManage:
      return false;

    case DASHBOARD_OPERATION_KEYS.studyView:
    case DASHBOARD_OPERATION_KEYS.subjectView:
    case DASHBOARD_OPERATION_KEYS.measurementView:
    case DASHBOARD_OPERATION_KEYS.questionnaireView:
      return (
        hasMemberScopedDashboardAccess(context, bridge) &&
        MEMBER_READ_ROLES.has(role)
      );

    case DASHBOARD_OPERATION_KEYS.studyManage:
    case DASHBOARD_OPERATION_KEYS.configurationManage:
      return false;

    case DASHBOARD_OPERATION_KEYS.resultView:
    case DASHBOARD_OPERATION_KEYS.exportView:
      return (
        (hasMemberScopedDashboardAccess(context, bridge) &&
          RESULTS_AND_EXPORT_ROLES.has(role)) ||
        hasInternalResultsBridge(bridge.email, context, bridge)
      );

    default:
      return false;
  }
}

export function canManageDashboardOrganisation(context, bridge = {}) {
  return hasDashboardOperationPermission(
    context,
    DASHBOARD_OPERATION_KEYS.organisationManage,
    bridge,
  );
}

export function canViewDashboardStudies(context, bridge = {}) {
  return hasDashboardOperationPermission(
    context,
    DASHBOARD_OPERATION_KEYS.studyView,
    bridge,
  );
}

export function canManageDashboardStudies(context, bridge = {}) {
  return hasDashboardOperationPermission(
    context,
    DASHBOARD_OPERATION_KEYS.studyManage,
    bridge,
  );
}

export function canViewDashboardSubjects(context, bridge = {}) {
  return hasDashboardOperationPermission(
    context,
    DASHBOARD_OPERATION_KEYS.subjectView,
    bridge,
  );
}

export function canViewDashboardMeasurements(context, bridge = {}) {
  return hasDashboardOperationPermission(
    context,
    DASHBOARD_OPERATION_KEYS.measurementView,
    bridge,
  );
}

export function canViewDashboardQuestionnaires(context, bridge = {}) {
  return hasDashboardOperationPermission(
    context,
    DASHBOARD_OPERATION_KEYS.questionnaireView,
    bridge,
  );
}

export function canViewDashboardPatientData(context, bridge = {}) {
  return (
    canViewDashboardSubjects(context, bridge) &&
    canViewDashboardMeasurements(context, bridge) &&
    canViewDashboardQuestionnaires(context, bridge)
  );
}

export function canSendDashboardSubjectReminders(context, bridge = {}) {
  return (
    SUBJECT_REMINDER_SEND_ROLES.has(resolveDashboardRole(context, bridge)) &&
    canViewDashboardSubjects(context, bridge)
  );
}

export function canCreateDashboardSubjectInvites(context, bridge = {}) {
  return (
    SUBJECT_REMINDER_SEND_ROLES.has(resolveDashboardRole(context, bridge)) &&
    canViewDashboardSubjects(context, bridge)
  );
}

export function canViewDashboardResults(context, bridge = {}) {
  return hasDashboardOperationPermission(
    context,
    DASHBOARD_OPERATION_KEYS.resultView,
    bridge,
  );
}

export function canExportDashboardData(context, bridge = {}) {
  return hasDashboardOperationPermission(
    context,
    DASHBOARD_OPERATION_KEYS.exportView,
    bridge,
  );
}

export function canManageDashboardConfiguration(context, bridge = {}) {
  return hasDashboardOperationPermission(
    context,
    DASHBOARD_OPERATION_KEYS.configurationManage,
    bridge,
  );
}
